import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { withFragments } from '@sorare/core/src/lib/gql';

import { RuleHelperFnReturnType } from '../types';
import { getCardEditionsCountRule_so5Rules } from './__generated__/getCardEditionsCountRule.graphql';

export const getCardEditionsCountRule = withFragments(
  (
    rules: getCardEditionsCountRule_so5Rules,
    error: string
  ): RuleHelperFnReturnType => {
    const rule = rules.cardEditionsCount;
    if (!rule?.displayedEditions?.length) {
      return null;
    }

    const cardEditionsCount = [];
    if (typeof rule.min === 'number' || typeof rule.max === 'number') {
      cardEditionsCount.push({
        min: rule.min,
        max: rule.max,
        editions: rule.displayedEditions
          .filter(edition => edition.max !== 0)
          .map(edition => edition.displayName),
      });
    }

    const editionsByMinMax = groupBy(
      rule.displayedEditions.filter(
        edition =>
          typeof edition.min === 'number' || typeof edition.max === 'number'
      ),
      edition => `${edition.min?.toString()}-${edition.max?.toString()}`
    );

    cardEditionsCount.push(
      ...Object.values(editionsByMinMax).map((editions = []) => ({
        min: editions[0].min,
        max: editions[0].max,
        editions: editions.map(({ displayName }) => displayName),
      }))
    );

    return {
      id: 'cardEditionsCount',
      label: (
        <FormattedMessage
          id="Rules.cardEditionsCount.label"
          defaultMessage="Special Edition"
        />
      ),
      description: (
        <Vertical as="span" gap={0}>
          {cardEditionsCount.map(({ min, max, editions }, i) => {
            let display = null;
            if (min && max) {
              display = 'between';
            } else if (max === 0) {
              display = 'no';
            } else if (max && max > 0) {
              display = 'max';
            } else if (min && min > 0) {
              display = 'min';
            }

            return (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i}>
                <FormattedMessage
                  id="Rules.cardEditionsCount.description"
                  defaultMessage="{display, select,
                    between {Between {min} and {max}}
                    min {Min {min}}
                    max {Max {max}}
                    no {No}
                    other {}
                  } {editions} {max, plural, one {card} other {cards}}"
                  values={{
                    display,
                    min,
                    max,
                    editions: editions.join(', '),
                  }}
                />
              </span>
            );
          })}
        </Vertical>
      ),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.12894 0.34631C5.55587 0.240399 5.00545 0.619103 4.89954 1.19217L2.98185 11.5684C2.87594 12.1415 3.25465 12.6919 3.82771 12.7978L11.437 14.2042C12.01 14.3101 12.5605 13.9314 12.6664 13.3583L14.5841 2.98201C14.69 2.40894 14.3113 1.85852 13.7382 1.75261L6.12894 0.34631Z"
            fill="url(#paint0_linear_8846_65038)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.12894 0.34631C5.55587 0.240399 5.00545 0.619103 4.89954 1.19217L2.98185 11.5684C2.87594 12.1415 3.25465 12.6919 3.82771 12.7978L11.437 14.2042C12.01 14.3101 12.5605 13.9314 12.6664 13.3583L14.5841 2.98201C14.69 2.40894 14.3113 1.85852 13.7382 1.75261L6.12894 0.34631Z"
            fill="black"
            fillOpacity="0.6"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.01484 1.60451C3.43373 1.56055 2.927 1.99598 2.88304 2.57709L2.08693 13.099C2.04296 13.6801 2.4784 14.1869 3.05951 14.2308L10.7756 14.8146C11.3567 14.8586 11.8634 14.4232 11.9074 13.8421L12.7035 3.32013C12.7475 2.73902 12.312 2.23229 11.7309 2.18832L4.01484 1.60451Z"
            fill="url(#paint1_linear_8846_65038)"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.01484 1.60451C3.43373 1.56055 2.927 1.99598 2.88304 2.57709L2.08693 13.099C2.04296 13.6801 2.4784 14.1869 3.05951 14.2308L10.7756 14.8146C11.3567 14.8586 11.8634 14.4232 11.9074 13.8421L12.7035 3.32013C12.7475 2.73902 12.312 2.23229 11.7309 2.18832L4.01484 1.60451Z"
            fill="black"
            fillOpacity="0.49"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.08743 3.01331C1.50466 3.01331 1.03223 3.48573 1.03223 4.06851V14.6205C1.03223 15.2033 1.50466 15.6757 2.08743 15.6757H9.82556C10.4083 15.6757 10.8808 15.2033 10.8808 14.6205V4.06851C10.8808 3.48573 10.4083 3.01331 9.82556 3.01331H2.08743Z"
            fill="url(#paint2_linear_8846_65038)"
          />
          <path
            d="M5.63755 7H6.26255L6.72726 8.43025L8.23112 8.43025L8.42425 9.02466L7.20761 9.90861L7.67233 11.3389L7.16669 11.7062L5.95005 10.8223L4.73341 11.7062L4.22777 11.3389L4.69248 9.90861L3.47583 9.02467L3.66897 8.43026L5.17283 8.43025L5.63755 7Z"
            fill="white"
          />
          <defs>
            <linearGradient
              id="paint0_linear_8846_65038"
              x1="11.0914"
              y1="12.561"
              x2="4.24863"
              y2="3.71039"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.0272" stopColor="#391885" />
              <stop offset="0.961" stopColor="#EB00FF" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_8846_65038"
              x1="10.2561"
              y1="13.2179"
              x2="2.50539"
              y2="5.15052"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.0272" stopColor="#391885" />
              <stop offset="0.961" stopColor="#EB00FF" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_8846_65038"
              x1="9.1871"
              y1="14.1227"
              x2="0.849809"
              y2="6.66309"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.0272" stopColor="#391885" />
              <stop offset="0.961" stopColor="#EB00FF" />
            </linearGradient>
          </defs>
        </svg>
      ),
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getCardEditionsCountRule_so5Rules on So5Rules {
        id
        cardEditionsCount {
          min
          max
          displayedEditions {
            min
            max
            displayName
          }
        }
      }
    ` as TypedDocumentNode<getCardEditionsCountRule_so5Rules>,
  }
);
